import mapKnownValues from "../utils/mapKnownValues";
import TransferSource, { TransferSourceResponse } from "./transferSource";
import TransferDestination, { AccountType, DestinationType, TransferDestinationResponse } from "./transferDestination";
import { TransferStatusTypes } from "./transfer";
import toCamel from "../utils/toCamel";
import { ScheduledPhoneCall } from "./calendly";

export enum NewTransferStatusType {
  WAIT_FOR_IRA_OPENING = "WAIT_FOR_IRA_OPENING",
  IRA_OPENING_ERROR = "IRA_OPENING_ERROR",
  PENDING_DESTINATION = "PENDING_DESTINATION",
  PENDING_ROTH_DESTINATION = "PENDING_ROTH_DESTINATION",
  PENDING_TRAD_DESTINATION = "PENDING_TRAD_DESTINATION",
  SCHEDULE_PHONE_CALL = "SCHEDULE_PHONE_CALL",
  PROCESSING = "PROCESSING",
  ADDITIONAL_INFORMATION_NEEDED = "ADDITIONAL_INFORMATION_NEEDED",
  INITIATED_AT_SOURCE = "INITIATED_AT_SOURCE",
  CHECK_SENT_TO_DESTINATION = "CHECK_SENT_TO_DESTINATION",
  COMPLETE = "COMPLETE",
  CANCELLED = "CANCELLED",
}

export enum TransferStatusFlow {
  UPDATE_DESTINATION = "UPDATE_DESTINATION",
  CONNECT_NEW_DESTINATION = "CONNECT_NEW_DESTINATION",
  CONNECT_NEW_DESTINATION_FROM_EXISTING_IRA = "CONNECT_NEW_DESTINATION_FROM_EXISTING_IRA",
}

export enum OpenLoanUserDecision {
  LOAN_CLOSED = "LOAN_CLOSED",
  TRANSFER_WITH_OPEN_LOAN = "ROLLOVER_WITH_OPEN_LOAN",
}

export enum TransferActions {
  linkDestination = "LINK_DESTINATION",
  linkRothDestination = "LINK_ROTH_DESTINATION",
  linkTraditionalDestination = "LINK_TRADITIONAL_DESTINATION",
  uploadDocument = "UPLOAD_DOC",
  openLoan = "OPEN_LOAN",
  phoneCall = "PHONE_CALL",
}

export enum TransferStatusScreens {
  operationsCheck = "OPERATIONS_CHECK",
  action = "ACTION",
  checkMailedToDestination = "CHECK_MAILED_TO_DESTINATION",
  initiated = "INITIATED",
  complete = "COMPLETE",
  cancelled = "CANCELLED",
  inQueue = "WAIT_FOR_OPERATIONS",
  waitForIRAOpening = "WAIT_FOR_IRA_OPENING",
}

export enum TransferInputReason {
  contactUs = "CONTACT_US",
  destinationConnectedSchedulePhoneCall = "DESTINATION_LINKED_SCHEDULE_PHONE_CALL",
  linkDestination = "LINK_DESTINATION",
  linkTraditionalDestination = "LINK_TRADITIONAL",
  linkRoth = "LINK_ROTH",
  loan = "LOAN",
  missingInformation = "MISSING_INFORMATION",
  missingInformationSchedulePhoneCall = "MISSING_INFORMATION_SCHEDULE_PHONE_CALL",
  phoneTransfer = "PHONE_TRANSFER",
  schedulePhoneCall = "SCHEDULE_PHONE_CALL",
  userInputNeeded = "USER_INPUT_NEEDED",
  uploadDoc = "UPLOAD_DOC",
}

export enum TransferUserInputReason {
  openLoan = "Open Loan",
  linkRothDestination = "Link Roth Destination",
  linkDestination = "Link Destination",
  linkTraditionalDestination = "Link Traditional Destination",
  schedulePhoneCall = "Schedule Phone Call",
  uploadDocument = "Upload Document",
}

export enum TransferCancelReason {
  invalidAccountForTransfer = "Invalid Account Type For Transfer",
  userCancelled = "User Cancelled",
  other = "Other",
}

export enum TransferInitiationMethod {
  phone = "PHONE",
  form = "FORM",
}

export type TransferInstructionResponse = {
  transferId: number;
  checkMailedToDestination?: boolean;
  destinations: TransferDestinationResponse[];
  source: TransferSourceResponse | null;
  transferCancelReason?: string | null;
  transferInstructionId: number;
  transferStatus?: TransferStatusTypes;
  transferInstructionStatus: NewTransferStatusType;
  transferExternalStatusDescription?: string;
  transferUserInputReason?: TransferInputReason | null;
  scheduledPhoneCall: ScheduledPhoneCall | null;
};

// The TransferInstruction class is responsible for the details of a transfer once it's initiated.
// The completed transfer will only have one source + destination, so if a transfer had multiple
// sources, they will be converted into multiple TransferInstruction classes once the transfer is
// initiated.
export default class TransferInstruction {
  checkMailedToDestination = false;
  destinations: TransferDestination[] = [];
  source: TransferSource | null = null;
  transferCancelReason: string | null = null;
  transferInstructionId = -1;
  transferId = -1;
  transferStatus: TransferStatusTypes = TransferStatusTypes.none;
  transferInstructionStatus: NewTransferStatusType = NewTransferStatusType.PROCESSING;
  transferUserInputReason: TransferInputReason | null = null;
  scheduledPhoneCall: ScheduledPhoneCall | null = null;
  transferExternalStatusDescription = "Manifest is processing your transfer; no action is needed.";
  adhocDocsFileNames: string[] = [];
  openLoanUserDecision?: OpenLoanUserDecision;

  constructor(params: Partial<TransferInstruction> = {}) {
    mapKnownValues(this, params);
  }

  get action() {
    const reason = this.transferUserInputReason;

    // If it's processing and we need to schedule a call, show the call screen with the scheduled information displayed
    if (this.transferStatus === TransferStatusTypes.processing && this.scheduledPhoneCall !== null) {
      return TransferActions.phoneCall;
    }
    if (this.transferStatus === TransferStatusTypes.userInputNeeded) {
      if (reason === TransferInputReason.loan) return TransferActions.openLoan;
      if (reason === TransferInputReason.destinationConnectedSchedulePhoneCall) return TransferActions.phoneCall;
      if (reason === TransferInputReason.schedulePhoneCall) return TransferActions.phoneCall;
      if (reason === TransferInputReason.linkDestination) return TransferActions.linkDestination;
      if (reason === TransferInputReason.linkRoth) return TransferActions.linkRothDestination;
      if (reason === TransferInputReason.linkTraditionalDestination) return TransferActions.linkTraditionalDestination;
      if (reason === TransferInputReason.uploadDoc) return TransferActions.uploadDocument;
    }
    return null;
  }

  get urlFriendlyStatus() {
    if (this.action) {
      return toCamel(this.action || "");
    }
    return toCamel(this.transferStatus);
  }

  // There can only be two possible destinations right now, if this changes, we will need to update this logic
  get rothDestination() {
    return this.destinations.find((destination) => destination.destinationType === DestinationType.rothDestination);
  }

  get traditionalDestination() {
    return this.destinations.find(
      (destination) => destination.destinationType === DestinationType.traditionalDestination
    );
  }

  get nonPendingIRADestinations() {
    return this.destinations.filter((destination) => {
      return destination.destinationAccountType === AccountType.ira && !destination.yetToOpen;
    });
  }

  get pendingIRADestinations() {
    return this.destinations.filter((destination) => {
      return destination.destinationAccountType === AccountType.ira && destination.yetToOpen;
    });
  }

  get destinationsYetToOpen() {
    return this.destinations.filter((destination) => destination.yetToOpen);
  }

  get transferStatusScreen() {
    if (this.action) return TransferStatusScreens.action;
    if (this.isInQueue) return TransferStatusScreens.inQueue;
    if (this.waitForIRAOpening) return TransferStatusScreens.waitForIRAOpening;
    if (this.transferStatus === TransferStatusTypes.processing) return TransferStatusScreens.operationsCheck;
    if (this.transferStatus === TransferStatusTypes.initiatedAtProvider) {
      if (this.checkMailedToDestination) return TransferStatusScreens.checkMailedToDestination;
      return TransferStatusScreens.initiated;
    }
    if (this.transferStatus === TransferStatusTypes.complete) return TransferStatusScreens.complete;
    return TransferStatusScreens.cancelled;
  }

  get needsToScheduleCall() {
    if (!this.transferUserInputReason) return false;
    return [
      TransferInputReason.destinationConnectedSchedulePhoneCall,
      TransferInputReason.phoneTransfer,
      TransferInputReason.schedulePhoneCall,
      TransferInputReason.missingInformationSchedulePhoneCall,
    ].includes(this.transferUserInputReason);
  }

  get checkMailed() {
    return this.checkMailedToDestination;
  }

  get isPaused() {
    if (
      this.transferStatus === TransferStatusTypes.waitForIRAOpening &&
      this.destinations[0].iraAccountOpeningData?.status === "ACTION_NEEDED"
    ) {
      return true;
    }
    if (this.transferUserInputReason === TransferInputReason.uploadDoc) {
      return this.adhocDocsFileNames.length === 0;
    }
    if (this.transferStatus === TransferStatusTypes.processing && this.scheduledPhoneCall !== null) {
      return false;
    }
    if (this.transferUserInputReason === TransferInputReason.schedulePhoneCall && this.scheduledPhoneCall) return false;
    return !!this.action;
  }

  get daysRemaining() {
    if (this.transferStatus === TransferStatusTypes.waitForIRAOpening) return 30;
    if (this.transferUserInputReason === TransferInputReason.schedulePhoneCall && this.scheduledPhoneCall) return 30;
    if (this.transferUserInputReason === TransferInputReason.uploadDoc && !this.isPaused) return 30;
    if (this.transferStatus === TransferStatusTypes.complete) return 0;
    if (this.transferStatus === TransferStatusTypes.cancelled) return 0;
    if (this.checkMailedToDestination) return 14;
    if (this.transferStatus === TransferStatusTypes.processing) return 30;
    if (this.transferStatus === TransferStatusTypes.initiatedAtProvider) return 20;
  }

  get completionPercentage() {
    if (this.transferUserInputReason === TransferInputReason.linkTraditionalDestination) return 15;
    if (this.transferUserInputReason === TransferInputReason.linkRoth) return 15;
    if (this.transferUserInputReason === TransferInputReason.linkDestination) return 15;
    if (this.transferUserInputReason === TransferInputReason.schedulePhoneCall) return 15;
    if (this.transferStatus === TransferStatusTypes.inQueue) return 15;
    if (this.transferStatus === TransferStatusTypes.complete) return 100;
    if (this.transferStatus === TransferStatusTypes.cancelled) return 0;
    if (this.transferStatus === TransferStatusTypes.processing) return 10;
    if (this.transferStatus === TransferStatusTypes.waitForIRAOpening) return 15;
    if (this.transferStatusScreen === TransferStatusScreens.checkMailedToDestination) return 80;
    return this.needsToLinkExternalIra || this.needsMissingInfoForPhoneCall || this.needsPhoneCallAfterDestination
      ? 20
      : 60;
  }

  get isCancelled() {
    return this.transferStatus === TransferStatusTypes.cancelled;
  }

  get isInQueue() {
    return this.transferStatus === TransferStatusTypes.inQueue;
  }

  get needsToLinkExternalIra() {
    return this.transferUserInputReason === TransferInputReason.linkTraditionalDestination;
  }

  get waitForIRAOpening() {
    return (
      this.transferStatus === TransferStatusTypes.waitForIRAOpening ||
      this.transferInstructionStatus === NewTransferStatusType.WAIT_FOR_IRA_OPENING
    );
  }

  get needsMissingInfoForPhoneCall() {
    return (
      (this.transferStatus === "USER_INPUT_NEEDED" &&
        this.transferUserInputReason === TransferInputReason.missingInformationSchedulePhoneCall) ??
      false
    );
  }

  get needsPhoneCallAfterDestination() {
    return (
      (this.transferStatus === "USER_INPUT_NEEDED" &&
        this.transferUserInputReason === TransferInputReason.destinationConnectedSchedulePhoneCall) ??
      false
    );
  }

  static fromAPI(response: TransferInstructionResponse) {
    return new TransferInstruction({
      ...response,
      scheduledPhoneCall: response.scheduledPhoneCall
        ? {
            ...response.scheduledPhoneCall,
            startTime: new Date(response.scheduledPhoneCall.startTime),
            endTime: new Date(response.scheduledPhoneCall.endTime),
          }
        : null,
      destinations: response.destinations.map((destination) => TransferDestination.fromApi(destination)),
      source: response.source ? TransferSource.fromApi(response.source) : null,
    });
  }
}
